// Thin fetch wrapper that attaches the AuthKit access token + parses JSON.
// Mirrors the arc-echo pattern so the auth flow + error shape is consistent.
// `<img src>` / `<a download>` can't set the Authorization header, so file
// routes accept ?token= as a fallback (see useApiSrc).
import { useAuth } from '@workos-inc/authkit-react'
import { useCallback, useEffect, useState } from 'react'

export type GetToken = () => Promise<string | undefined>

const BASE = import.meta.env.VITE_API_URL || ''

export async function apiFetch<T = unknown>(getAccessToken: GetToken, path: string, options: RequestInit = {}): Promise<T> {
  // NEVER pass { refresh: true } to AuthKit's getAccessToken — that triggers
  // a refresh-token rotation race against api.workos.com/.../authenticate
  // which returns 400 invalid_grant (and Firefox surfaces it as "CORS missing
  // Allow-Origin" because WorkOS skips CORS headers on 4xx). On a 401, retry
  // ONCE with a plain (cached) token call; if still 401, surface to the caller.
  async function attempt(): Promise<Response> {
    let token: string | undefined
    try { token = await getAccessToken() } catch { /* anon */ }
    const hasBody = options.body != null
    const headers: Record<string, string> = { ...((options.headers as Record<string, string>) ?? {}) }
    if (hasBody) headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
    if (token) headers['Authorization'] = `Bearer ${token}`
    return fetch(`${BASE}${path}`, { ...options, headers, credentials: 'include' })
  }
  let res = await attempt()
  if (res.status === 401) res = await attempt()
  const text = await res.text()
  let body: unknown = null
  if (text) { try { body = JSON.parse(text) } catch { /* non-JSON */ } }
  if (!res.ok) {
    const b = body as { message?: string; error?: string } | null
    const msg = b?.message ?? b?.error ?? `HTTP ${res.status}`
    const err = new Error(msg) as Error & { status?: number; body?: unknown }
    err.status = res.status; err.body = body; throw err
  }
  return body as T
}

// Build a stable, signed file URL (or fall back to ?token= for downloads).
// Use for <img src> + <a download> where headers aren't available.
export function useApiSrc() {
  const { getAccessToken } = useAuth()
  const [token, setToken] = useState('')
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try { const t = await getAccessToken(); if (!cancelled) setToken(t || '') } catch { /* anon */ }
    })()
    return () => { cancelled = true }
  }, [getAccessToken])
  return useCallback((path?: string | null) => {
    if (!path) return path ?? ''
    if (!path.startsWith('/api/')) return path
    if (/[?&]sig=/.test(path)) return path
    if (!token) return path
    const sep = path.includes('?') ? '&' : '?'
    return `${path}${sep}token=${encodeURIComponent(token)}`
  }, [token])
}
